/**
 * Admin - Users Management Page
 *
 * List all users with management options
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import { dedupeRequest } from '../../lib/request-cache';
import {
  Users,
  ArrowLeft,
  Plus,
  Search,
  ChevronRight,
  Database,
  FileText,
  Mail,
  Clock,
  XCircle,
} from 'lucide-react';

interface UserProfile {
  user_id: string;
  email: string | null;
  display_name: string | null;
  voice_profile: any;
  tone_preference: string | null;
  narrative_style: string | null;
  topics_include: string[] | null;
  topics_exclude: string[] | null;
  onboarding_completed: boolean;
  onboarding_step: number;
  is_admin: boolean;
  role: string;
  invited_by: string | null;
  invitation_accepted_at: string | null;
  plan_name: string | null;
  plan_status: string | null;
  trial_ends_at: string | null;
  sources_count: number;
  posts_count: number;
  logto: {
    name: string | null;
    primaryEmail: string | null;
  };
  created_at: string;
  updated_at: string;
}

interface UsersResponse {
  data: UserProfile[];
}

interface Invitation {
  id: string;
  email: string;
  name: string | null;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  metadata: { plan_id?: string; plan_name?: string; trial_days?: number } | null;
}

interface InvitationsResponse {
  data: Invitation[];
}

export function AdminUsersPage() {
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const loadInvitations = async () => {
    try {
      const result = await dedupeRequest('admin-invitations', () =>
        apiRequest<InvitationsResponse>('/api/admin/invitations', { method: 'GET' })
      );
      setInvitations(result.data || []);
    } catch {
      // Non-critical — don't block the page
    }
  };

  useEffect(() => {
    // Prevent duplicate loads in React Strict Mode
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    let cancelled = false;

    const loadUsers = async () => {
      try {
        setLoading(true);
        const result = await dedupeRequest('admin-users', () =>
          apiRequest<UsersResponse>('/api/admin/users', {
            method: 'GET',
          })
        );
        if (!cancelled) {
          setUsers(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load users');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUsers();
    loadInvitations();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleRevokeInvitation = async (id: string) => {
    if (!confirm('Revoke this invitation? The user will no longer be able to sign up.')) return;
    setRevokingId(id);
    try {
      await apiRequest(`/api/admin/invitations/${id}`, { method: 'DELETE' });
      setInvitations(prev => prev.filter(inv => inv.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invitation');
    } finally {
      setRevokingId(null);
    }
  };

  const handleResendInvitation = async (id: string) => {
    setResendingId(id);
    setError(null);
    try {
      const result = await apiRequest<{ data: { email_sent: boolean; new_expires_at: string } }>(
        `/api/admin/invitations/${id}/resend`,
        { method: 'POST' }
      );
      // Update the invitation in local state with the new expiration
      setInvitations(prev =>
        prev.map(inv =>
          inv.id === id
            ? { ...inv, expires_at: result.data.new_expires_at }
            : inv
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase();
    const name = user.display_name || user.logto?.name || '';
    const email = user.email || user.logto?.primaryEmail || '';
    return (
      name.toLowerCase().includes(term) ||
      email.toLowerCase().includes(term) ||
      user.user_id.toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  User Management
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage users, roles, and subscriptions
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/users/create')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Invite User
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or user ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-amber-200 flex items-center gap-2">
              <Mail className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-medium text-amber-800">
                Pending Invitations ({pendingInvitations.length})
              </h3>
            </div>
            <div className="divide-y divide-amber-100">
              {pendingInvitations.map(inv => {
                const isExpired = new Date(inv.expires_at) < new Date();
                return (
                  <div key={inv.id} className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                        isExpired ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
                      }`}>
                        {isExpired ? <XCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {inv.name || inv.email}
                        </div>
                        <div className="text-xs text-gray-500">
                          {inv.email}
                          {inv.metadata?.plan_name && (
                            <span className="ml-2 text-amber-600">• {inv.metadata.plan_name}</span>
                          )}
                          {isExpired && (
                            <span className="ml-2 text-red-600">• Expired</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleResendInvitation(inv.id); }}
                        disabled={resendingId === inv.id || revokingId === inv.id}
                        className="text-xs text-mazo-600 hover:text-mazo-800 px-2 py-1 rounded hover:bg-mazo-50 transition-colors disabled:opacity-50"
                      >
                        {resendingId === inv.id ? 'Sending...' : 'Resend'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRevokeInvitation(inv.id); }}
                        disabled={revokingId === inv.id || resendingId === inv.id}
                        className="text-xs text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {revokingId === inv.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-mazo-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading users...</p>
          </div>
        )}

        {/* Users Table */}
        {!loading && filteredUsers.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try a different search term' : 'Get started by inviting a user'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/admin/users/create')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Invite User
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && filteredUsers.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trial
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sources
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posts
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const displayName = user.display_name || user.logto?.name || 'Unknown';
                  const email = user.email || user.logto?.primaryEmail || user.user_id;

                  return (
                    <tr
                      key={user.user_id}
                      onClick={() => navigate(`/admin/users/${user.user_id}/edit`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {displayName}
                          </div>
                          <div className="text-sm text-gray-500">{email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.is_admin
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-mazo-100 text-mazo-800'
                          }`}
                        >
                          {user.is_admin ? 'Admin' : user.role || 'User'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {user.plan_name ? (
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.plan_status === 'trialing'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {user.plan_name}
                            </span>
                            {user.plan_status === 'trialing' && (
                              <span className="text-xs text-blue-600">Trial</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No plan</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {user.plan_status === 'trialing' && user.trial_ends_at ? (
                          (() => {
                            const daysRemaining = Math.ceil(
                              (new Date(user.trial_ends_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                            );
                            if (daysRemaining < 0) {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Expired
                                </span>
                              );
                            }
                            const colorClass =
                              daysRemaining <= 3
                                ? 'text-red-600'
                                : daysRemaining <= 7
                                ? 'text-amber-600'
                                : 'text-green-600';
                            return (
                              <span className={`text-sm font-medium ${colorClass}`}>
                                {daysRemaining}d left
                              </span>
                            );
                          })()
                        ) : (
                          <span className="text-sm text-gray-400">&mdash;</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-900">
                          <Database className="w-4 h-4 text-gray-400" />
                          {user.sources_count}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-900">
                          <FileText className="w-4 h-4 text-gray-400" />
                          {user.posts_count}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats */}
        {!loading && users.length > 0 && (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-4">
            <div className="bg-white overflow-hidden rounded-lg border border-gray-200 px-4 py-5">
              <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">{users.length}</dd>
            </div>
            <div className="bg-white overflow-hidden rounded-lg border border-gray-200 px-4 py-5">
              <dt className="text-sm font-medium text-gray-500 truncate">Pending Invites</dt>
              <dd className="mt-1 text-3xl font-semibold text-amber-600">{pendingInvitations.length}</dd>
            </div>
            <div className="bg-white overflow-hidden rounded-lg border border-gray-200 px-4 py-5">
              <dt className="text-sm font-medium text-gray-500 truncate">With Active Plan</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {users.filter((u) => u.plan_name).length}
              </dd>
            </div>
            <div className="bg-white overflow-hidden rounded-lg border border-gray-200 px-4 py-5">
              <dt className="text-sm font-medium text-gray-500 truncate">Admins</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {users.filter((u) => u.is_admin).length}
              </dd>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
